import { useSelector } from 'react-redux'
import { CLAIM } from '@constants/index'
import { useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { claimRecordIdSelector } from '@redux/slices/claimStatusSlice'
import { ClaimRecord } from '@typesApp/ClaimRecord'

type ReadyCallback = (claimRecord: ClaimRecord) => boolean
type HandleResultCallback = (claimRecord: ClaimRecord) => void
type ErrorCallback = (error: unknown) => void
type PollFunctionOptions = {
  claimRecordId?: string
}

type PollClaimRecordFunction = (
  readyCallback: ReadyCallback,
  handleResultCallback: HandleResultCallback,
  errorCallback: ErrorCallback,
  options?: PollFunctionOptions
) => void

export const usePollClaimRecord = (pollingInterval = CLAIM.POLLING_INTERVAL_MS): PollClaimRecordFunction => {
  const claimRecordId = useSelector(claimRecordIdSelector)
  const [fetchClaimRecord] = useLazyGetClaimRecordQuery()

  const pollClaimRecord = (
    readyCallback: ReadyCallback,
    handleResultCallback: HandleResultCallback,
    errorCallback: ErrorCallback,
    options?: PollFunctionOptions
  ) => {
    // Note: In some cases (e.g. sync insurance) where claim record is created just
    // prior to the polling, redux state may not have been updated yet so
    // we need to allow caller to pass in the claim record id.
    fetchClaimRecord(options?.claimRecordId ?? claimRecordId)
      .unwrap()
      .then(claimRecord => {
        if (readyCallback(claimRecord)) {
          handleResultCallback(claimRecord)
        } else {
          setTimeout(
            () => pollClaimRecord(readyCallback, handleResultCallback, errorCallback, options),
            pollingInterval
          )
        }
      })
      .catch(error => {
        console.error('Poll claim record error', error)
        errorCallback(error)
      })
  }

  return pollClaimRecord
}
